import React, { useState, useEffect } from 'react';
import Carousel from 'nuka-carousel';
import MediaQuery from 'react-responsive';
import classnames from 'classnames';
import smoothscroll from 'smoothscroll-polyfill';
import ShowMore from '../video-hub/components/show-more';

import nextSlideIcon from '../../images/icons/icon-next-article-blue.svg';
import previousSlideIcon from '../../images/icons/icon-prev-article-blue.svg';

import './index.scss';

const {
  i18n: {
    __,
  },
} = wp;

/**
 * Render a slide control button.
 *
 * @param {Object} nextSlide The next slide.
 * @param {String} icon The SVG string.
 * @param {String} altText The alternate image text.
 */
const renderControlButton = (nextSlide, icon, altText) => (
  <button
    className="video-slider__control"
    onClick={nextSlide}
    type="button"
  >
    <img
      src={icon}
      alt={altText}
    />
  </button>
);

/**
 * Scroll to the video.
 */
const scrollToVideo = () => {
  // scroll to player.
  smoothscroll.polyfill();
  document.getElementsByClassName('section-content--primary')[0].scrollIntoView({ behavior: 'smooth' }); // eslint-disable-line max-len
};

const liveLink = 'telemundo' === nbc.brand ? '/en-vivo' : '/live';

/**
 * When a slide is clicked.
 */
const handleOnClick = (disabled) => {
  if (! disabled) {
    window.location = liveLink; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
  }
};

/**
 * When the enter key is pressed on a slide.
 *
 * @param e
 * @returns {Function}
 */
const handleOnKeyDown = (e) => {
  if ('Enter' !== e.key) {
    return;
  }

  scrollToVideo();
};

/**
 * Display show on tablet/desktop.
 *
 * @param {String} date Selected Day.
 * @param {Array} days days.
 * @returns {function}
 */
const displayShow = (date, days, disabled) => (show, i) => {
  const cssClasses = classnames({
    'tabbed-content__section-schedule__show': true,
    'now-show': 0 === i && date === days[0],
  });
  return (
    <div
      className={cssClasses}
      key={show.start}
      role="button"
      tabIndex={0}
      onClick={() => { handleOnClick(disabled); }}
      onKeyDown={handleOnKeyDown}
    >
      <strong>
        {0 === i && date === days[0]
          ? __('NOW', 'nbc') : show.startPretty}
      </strong>
      <div className="tabbed-content__section-schedule__show__image">
        <img alt={show.title} src={show.image} />
        <span className="show_title">{show.title}</span>
      </div>
    </div>
  );
};

/**
 * Display show on mobile.
 *
 * @param {String} date Selected Day.
 * @param {Array} days days.
 * @returns {function}
 */
const displayMobileShow = (date, days, disabled) => (show, i) => {
  const cssClasses = classnames({
    'tabbed-content__section-schedule__show': true,
    'now-show': 0 === i && date === days[0],
  });
  return (
    <div
      className={cssClasses}
      key={show.start}
      role="button"
      tabIndex={0}
      onClick={() => { handleOnClick(disabled); }}
      onKeyDown={handleOnKeyDown}
    >
      <div
        className="tabbed-content__section-schedule__show__image"
      >
        <img alt={show.title} src={show.image} />
      </div>
      <div
        className="tabbed-content__section-schedule__show__text"
      >
        <strong>
          {0 === i && date === days[0]
            ? __('NOW', 'nbc') : show.startPretty}
        </strong>
        <span className="show_title">{show.title}</span>
      </div>
    </div>
  );
};

/**
 * Schedule component.
 */
const Schedule = ({
  tvSchedule, // if true, disables liveLink linkout.
}) => {
  const [{ live, weekly }, setData] = useState({
    live: {},
    weekly: {
      notloaded: true,
    },
  });
  const [date, setDate] = useState(false);
  const [days, setDays] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [showMoreClicked, toggleShowMore] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const { apiFetch } = wp;
      const result = await apiFetch({
        path: '/nbc/v1/gracenote',
      });
      setData(result);
      const options = Object.keys(result.live);
      setDays(options);
      setDate(options[0]);
    }
    fetchData();
  }, []);

  /**
   * Handle clicking show more on mobile.
   */
  const handleShowMoreClicked = () => {
    toggleShowMore((prevState) => ! prevState);
  };

  if (! date) {
    return (<div />);
  }

  const currentData = live[date] ? live[date] : [];

  return (
    <div className="tabbed-content__sched-container">
      <select
        className="tabbed-content__select tabbed-content__select__sched"
        value={date}
        onChange={
          (e) => {
            setDate(e.currentTarget.value);
            toggleShowMore(false);
          }}
      >
        {
          days.map((day) => <option value={day}>{day}</option>)
        }
      </select>
      <h3
        className="tabbed-content__section-title"
      >
        {__('Full Schedule', 'nbc')}
      </h3>
      <MediaQuery minWidth={1024}>
        <Carousel
          className="tabbed-content__section-schedule"
          slideIndex={slideIndex}
          afterSlide={(index) => setSlideIndex(index)}
          dragging={false}
          cellSpacing={0}
          initialSlideHeight={180}
          width="100%"
          slidesToShow={5}
          slidesToScroll={5}
          renderBottomCenterControls={() => {}}
          renderTopLeftControls={() => null}
          renderTopRightControls={() => null}
          renderCenterRightControls={({ currentSlide, nextSlide }) => (
            currentSlide === currentData.length - 1 ? null :
              renderControlButton(nextSlide, nextSlideIcon, 'next'))}
          renderCenterLeftControls={({ currentSlide, previousSlide }) =>
            (0 !== currentSlide ? renderControlButton(
              previousSlide,
              previousSlideIcon,
              'previous'
            ) : null)}
        >
          {
            currentData.map(displayShow(date, days, tvSchedule))
          }
        </Carousel>
      </MediaQuery>
      <MediaQuery minWidth={768} maxWidth={1023}>
        <Carousel
          className="tabbed-content__section-schedule"
          slideIndex={slideIndex}
          afterSlide={(index) => setSlideIndex(index)}
          dragging={false}
          cellSpacing={0}
          initialSlideHeight={180}
          width="100%"
          slidesToShow={4}
          slidesToScroll={4}
          renderBottomCenterControls={() => {}}
          renderTopLeftControls={() => null}
          renderTopRightControls={() => null}
          renderCenterRightControls={({ currentSlide, nextSlide }) => (
            currentSlide === currentData.length - 1 ? null :
              renderControlButton(nextSlide, nextSlideIcon, 'next'))}
          renderCenterLeftControls={({ currentSlide, previousSlide }) =>
            (0 !== currentSlide ? renderControlButton(
              previousSlide,
              previousSlideIcon,
              'previous'
            ) : null)}
        >
          {
            currentData.map(displayShow(date, days, tvSchedule))
          }
        </Carousel>
      </MediaQuery>
      <MediaQuery maxWidth={767}>
        {
          ! showMoreClicked && [
            currentData
              .slice(0, 4)
              .map(displayMobileShow(date, days, tvSchedule)),
            (! showMoreClicked && 4 < currentData.length) && <ShowMore
              key="live-show-more"
              cssClass="show-more"
              showMore={handleShowMoreClicked}
              disabled={false}
            />,
          ]
        }
        {
          showMoreClicked &&
          currentData.map(displayMobileShow(date, days, tvSchedule))
        }
      </MediaQuery>

      {! weekly.notloaded &&
        <div className="tabbed-content__section-coverage">
          <h3
            className="tabbed-content__section-title"
          >
            {weekly.heading}
          </h3>
          <p>{weekly.description}</p>
          <div className="tabbed-content__section-coverage__sched">
            {
              weekly.schedule && weekly.schedule.map((schedule, i) => (
                <table
                  className="tabbed-content__section-coverage__sched__col"
                  key={i} // eslint-disable-line react/no-array-index-key
                >
                  <tbody>
                    <tr>
                      <th scope="col">{schedule.heading}</th>
                    </tr>
                    {schedule.list
                      .map((item) => (
                        <tr key={item.key}>
                          <th scope="row">{item.key}</th>
                          <td>{item.value}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ))
            }
          </div>
        </div>
      }
    </div>
  );
};

export default Schedule;
